import type { SurveyAttribute } from '@engagement-web-app/common';
import classNames from 'classnames';
import { ChevronDown, FileText, SlidersHorizontal } from 'lucide-react';
import { Popover } from 'radix-ui';
import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Dialog, DialogButtonGroup, DialogClose } from '@/components/Dialog';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { TextButton } from '@/components/TextButton';
import { ToggleButton } from '@/components/ToggleButton';
import { usePreventScrollLock } from '@/hooks';
import { hiddenInPrint } from '@/styles/print.css';
import * as styles from './AttributeFilter.css';

// Options beyond this count are reachable via each setting's search-and-add box.
const VISIBLE_OPTION_COUNT = 4;

const MAX_SELECTED_OPTIONS = 10;

type AttributeOption = SurveyAttribute['options'][number];

type AttributeFilterBaseProps = {
    attributes: SurveyAttribute[];
    selectedOptionIds: string[];
};

type AttributeFilterProps =
    | (AttributeFilterBaseProps & {
          restrictToSingleSetting?: false;
          onSelectionChange: (selectedOptions: AttributeOption[]) => void;
      })
    | (AttributeFilterBaseProps & {
          /** Once any option is selected, every other setting's row is disabled
           * until the selection is cleared back to empty. */
          restrictToSingleSetting: true;
          /** `settingId` is the currently locked-in setting, or `null` once the
           * selection has been cleared back to empty. */
          onSelectionChange: (selectedOptions: AttributeOption[], settingId: string | null) => void;
      });

export function AttributeFilter(props: AttributeFilterProps) {
    const { attributes, selectedOptionIds, restrictToSingleSetting } = props;

    const [isOpen, setIsOpen] = useState(false);
    // Selection only takes effect once the user hits the confirm button, so
    // toggling within the dialog operates on a local copy.
    const [pendingOptionIds, setPendingOptionIds] = useState(selectedOptionIds);
    // Options revealed as chips this session (pre-selected on open, or added via
    // search) — kept visible even after being toggled off, until the dialog reopens.
    const [pinnedOptionIds, setPinnedOptionIds] = useState<string[]>(selectedOptionIds);

    // Derived (not state) so it can never drift out of sync with pendingOptionIds.
    // These are exclusively used when `restrictToSingleSetting` is true.
    const activeSetting = restrictToSingleSetting
        ? (attributes.find((a) => a.options.some((o) => pendingOptionIds.includes(o.id))) ?? null)
        : null;
    const totalOptionsInSelectedSetting = restrictToSingleSetting ? activeSetting?.options.length : 0;

    function handleOpenChange(open: boolean) {
        setIsOpen(open);
        if (open) {
            setPendingOptionIds(selectedOptionIds);
            setPinnedOptionIds(selectedOptionIds);
        }
    }

    function handleToggle(optionId: string) {
        setPendingOptionIds((current) => {
            if (current.includes(optionId)) return current.filter((id) => id !== optionId);
            if (current.length >= MAX_SELECTED_OPTIONS) return current;
            if (restrictToSingleSetting && current.length > 0) {
                const targetSetting = attributes.find((a) => a.options.some((o) => o.id === optionId));
                if (targetSetting && targetSetting.id !== activeSetting?.id) return current;
            }
            return [...current, optionId];
        });
        setPinnedOptionIds((current) => (current.includes(optionId) ? current : [...current, optionId]));
    }

    function handleClear(optionIds: string[]) {
        const idsToClear = new Set(optionIds);
        setPendingOptionIds((current) => current.filter((id) => !idsToClear.has(id)));
    }

    function handleResetAll() {
        setPendingOptionIds([]);
    }

    const optionsInNaturalOrder = useMemo(() => attributes.flatMap((a) => a.options), [attributes]);

    // Narrowing only works when both sides of the check come from `props`
    // directly — see the note above `AttributeFilterProps`.
    function handleConfirm() {
        const selectedOptions = optionsInNaturalOrder.filter((o) => pendingOptionIds.includes(o.id));

        if (props.restrictToSingleSetting) {
            props.onSelectionChange(selectedOptions, activeSetting?.id ?? null);
        } else {
            props.onSelectionChange(selectedOptions);
        }
    }

    return (
        <Dialog
            trigger={
                restrictToSingleSetting ? (
                    <div className={classNames(styles.singleSettingTrigger.root, hiddenInPrint)}>
                        <button type="button" className={styles.singleSettingTrigger.content}>
                            {activeSetting?.name ?? '絞り込み'}
                            {activeSetting && ` (${selectedOptionIds.length}/${totalOptionsInSelectedSetting})`}
                        </button>
                        <span className={styles.singleSettingTrigger.caret}>
                            <Icon icon={ChevronDown} size="sm" />
                        </span>
                    </div>
                ) : (
                    <Button variant="secondary" className={hiddenInPrint}>
                        <Icon icon={SlidersHorizontal} size="sm" />
                        <span>
                            絞り込み{' '}
                            {selectedOptionIds.length > 0
                                ? `(${selectedOptionIds.length}/${MAX_SELECTED_OPTIONS})`
                                : ''}
                        </span>
                    </Button>
                )
            }
            title={
                <>
                    <Icon icon={SlidersHorizontal} size="lg" />
                    　絞り込み
                </>
            }
            open={isOpen}
            onOpenChange={handleOpenChange}
        >
            <p className={styles.note}>
                選択可能な件数は最大{MAX_SELECTED_OPTIONS}件までです。
                {restrictToSingleSetting && ' 選択できるのは1つの設定内の項目のみです。'}
            </p>

            <Flex direction="column">
                {attributes.map((attribute) => {
                    const topOptions = attribute.options.slice(0, VISIBLE_OPTION_COUNT);

                    const pinnedExtras = attribute.options.filter(
                        (o) => pinnedOptionIds.includes(o.id) && !topOptions.some((t) => t.id === o.id)
                    );

                    const visibleOptions = [...topOptions, ...pinnedExtras];

                    const isLocked =
                        restrictToSingleSetting && activeSetting !== null && activeSetting.id !== attribute.id;

                    return (
                        <Flex
                            key={attribute.id}
                            direction="column"
                            gap="3"
                            className={classNames(styles.settingRow, { [styles.settingRowLocked]: isLocked })}
                        >
                            <Flex justify="space-between" align="center">
                                <span className={styles.settingName}>
                                    {attribute.name} ({attribute.options.length})
                                </span>
                                <TextButton
                                    disabled={isLocked}
                                    onClick={() => handleClear(attribute.options.map((o) => o.id))}
                                >
                                    クリア
                                </TextButton>
                            </Flex>

                            <Flex gap="2" wrap="wrap" align="center">
                                {visibleOptions.map((option) => {
                                    const isSelected = pendingOptionIds.includes(option.id);
                                    return (
                                        <ToggleButton
                                            key={option.id}
                                            pressed={isSelected}
                                            onPressedChange={() => handleToggle(option.id)}
                                            disabled={
                                                isLocked ||
                                                (!isSelected && pendingOptionIds.length >= MAX_SELECTED_OPTIONS)
                                            }
                                        >
                                            {option.name}
                                        </ToggleButton>
                                    );
                                })}

                                <AttributeOptionSearch
                                    options={attribute.options}
                                    excludedOptionIds={visibleOptions.map((o) => o.id)}
                                    disabled={isLocked || pendingOptionIds.length >= MAX_SELECTED_OPTIONS}
                                    onAdd={handleToggle}
                                />
                            </Flex>
                        </Flex>
                    );
                })}
            </Flex>

            <DialogButtonGroup
                extras={
                    <Flex align="center" gap="3">
                        <TextButton onClick={handleResetAll}>すべてクリア</TextButton>
                        <span className={styles.selectedCount}>
                            {pendingOptionIds.length}/{MAX_SELECTED_OPTIONS} 件選択中
                        </span>
                    </Flex>
                }
            >
                <DialogClose>
                    <Button type="button" variant="secondary">
                        キャンセル
                    </Button>
                </DialogClose>
                <DialogClose>
                    <Button type="button" variant="primary" onClick={handleConfirm}>
                        <Icon icon={FileText} size="sm" />
                        適用する
                    </Button>
                </DialogClose>
            </DialogButtonGroup>
        </Dialog>
    );
}

/** A placeholder for the default (non-`restrictToSingleSetting`) trigger button. */
export function AttributeFilterSkeleton() {
    return <Skeleton.Box width={110} height={36} />;
}

type AttributeOptionSearchProps = {
    options: AttributeOption[];
    excludedOptionIds: string[];
    /** True when no further selection can be made right now — either the cap is
     * reached, or (in single-setting mode) this isn't the locked-in setting. The
     * component doesn't need to know which. */
    disabled: boolean;
    onAdd: (optionId: string) => void;
};

/**
 * A search box for a single setting's options. Focusing it opens a dropdown
 * of options not already shown as a chip underneath (typing narrows the
 * list); picking one adds it to the selection.
 */
function AttributeOptionSearch({ options, excludedOptionIds, disabled, onAdd }: AttributeOptionSearchProps) {
    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    usePreventScrollLock();

    const trimmedQuery = query.trim().toLowerCase();
    const availableOptions = options.filter((option) => !excludedOptionIds.includes(option.id));
    const matches = trimmedQuery
        ? availableOptions.filter((option) => option.name.toLowerCase().includes(trimmedQuery))
        : availableOptions;

    function handleSelect(optionId: string) {
        onAdd(optionId);
        setQuery('');
    }

    return (
        // `open` is driven entirely by our own focus tracking below — Radix's
        // built-in outside-focus detection doesn't recognise `Popover.Anchor`
        // (unlike `Trigger`) as part of the popover, so it would otherwise
        // treat the still-focused input as a reason to close right after the
        // dropdown mounts. Not wiring `onOpenChange` keeps that from fighting
        // our own state.
        <Popover.Root open={isFocused}>
            <Popover.Anchor>
                <SearchInput
                    placeholder="検索と追加"
                    value={query}
                    disabled={disabled}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') e.currentTarget.blur();
                    }}
                />
            </Popover.Anchor>

            <Popover.Portal>
                <Popover.Content
                    className={styles.searchDropdown}
                    align="start"
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    // Suppress the browser's default focus-shift on mousedown anywhere in
                    // the popover — including on its own scrollbar, which would otherwise
                    // blur the search input (closing the popover) before a drag can register.
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {matches.length > 0 ? (
                        matches.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={styles.searchResultItem}
                                disabled={disabled}
                                onClick={() => handleSelect(option.id)}
                            >
                                {option.name}
                            </button>
                        ))
                    ) : (
                        <span className={styles.searchEmptyState}>該当する項目がありません</span>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
